import { BooleanString, CreateTicketForm, EventCanGoLive, TicketBody } from './types';
import { convertDateWithTimezoneToUTC, convertUTCToDate, keepUpperCase } from 'utils/utils';
import { date, number, object, string } from 'yup';

import { EventData } from 'features/common/types';
import { TFunction } from 'react-i18next';
import { camelizeKeys } from 'humps';

export const NEW_TICKET = 'new-ticket';

const yesterday = new Date();
yesterday.setDate(yesterday.getDate() - 1);
yesterday.setHours(0, 0, 0, 0);
yesterday.setMinutes(0);

export const defaultValues: CreateTicketForm = {
	amount: undefined,
	buyerAbsorbPaymentFee: 'false',
	buyerAbsorbTixologistFee: 'false',
	initialPrice: null,
	name: undefined,
	poolId: undefined,
	bundleSize: undefined,
	secondaryRoyalty: undefined,
	sellingTime: yesterday,
	offSellingTime: null,
	description: '',
	allowSecondarySales: false,
	template: undefined,
	ticketImage: undefined,
	ticketVisibility: true,
	image: '',
	displayType: '',
	privateLink: false,
	hiddenCode: null,
	showImage: false,
	seatsCategoryKey: undefined,
	presaleCode: '',
	alwaysShowTickets: false,
	payWhatYouWant: false,
	minPrice: null,
	maxPrice: null,
	maxQuantity: null,
	percentageFee: null,
	fixedFee: null,
	color: undefined,
};

export const validationSchema = (t: TFunction<'translation', undefined>) =>
	object().shape({
		name: string().required(t('ticketDetails-requiredTicketTypeName')),
		initialPrice: number().typeError(t('ticketDetails-requiredTicketPrice')).required(),
		amount: number().typeError(t('ticketDetails-requiredQuantity')).required(),
		sellingTime: date().typeError(t('ticketDetails-requiredActiveTime')).required(),
		poolId: string().typeError(t('ticketDetails-requiredPool')).optional(),
	});

export const createBody = (
	ticketForm: CreateTicketForm,
	eventId: number,
	timezone: string,
	ticketId?: number
): TicketBody => {
	const _sellingTime = new Date(ticketForm.sellingTime ?? 0);
	const _offSellingTime = ticketForm.offSellingTime
		? convertDateWithTimezoneToUTC(new Date(ticketForm.offSellingTime), timezone)
		: null;
	const poolId =
		!isNaN(Number(ticketForm.poolId)) && Number(ticketForm.poolId) > 0 ? Number(ticketForm.poolId) : undefined;
	const _ticketForm = {
		allowSecondarySales: ticketForm.allowSecondarySales,
		amount: Number(ticketForm.amount),
		buyerAbsorbPaymentFee: ticketForm.buyerAbsorbPaymentFee === 'true',
		buyerAbsorbTixologistFee: ticketForm.buyerAbsorbTixologistFee === 'true',
		description: ticketForm.description,
		ticketVisibility: ticketForm.ticketVisibility,
		privateLink: ticketForm.privateLink,
		hidden_code: ticketForm.hiddenCode,
		showImage: ticketForm.showImage,
		seatsCategoryKey: ticketForm.seatsCategoryKey ? String(ticketForm.seatsCategoryKey) : undefined,
		presaleCode: ticketForm.presaleCode,
		alwaysShowTickets: ticketForm.alwaysShowTickets,
		eventId,
		//PUT
		ID: ticketId ?? undefined,
		initialPrice: Number(ticketForm.initialPrice),
		name: ticketForm.name ?? '',
		tickets_pool_id: poolId,
		bundleSize: ticketForm.bundleSize ? Number(ticketForm.bundleSize) : undefined,
		secondaryRoyalty: Number(ticketForm.secondaryRoyalty),
		sellingTime: convertDateWithTimezoneToUTC(_sellingTime, timezone),
		offSellingTime: _offSellingTime,
		image: ticketForm.image ?? '',
		displayType: ticketForm.displayType ?? '',
		template: {
			image: ticketForm.image,
			displayType: ticketForm.displayType,
		},
		payWhatYouWant: ticketForm.payWhatYouWant,
		minPrice: Number(ticketForm.minPrice),
		maxPrice: Number(ticketForm.maxPrice),
		max_quantity: ticketForm.maxQuantity ? Number(ticketForm.maxQuantity) : null,
		percentageFee: ticketForm.percentageFee !== null ? Number(ticketForm.percentageFee) : null,
		fixedFee: ticketForm.fixedFee !== null ? Number(ticketForm.fixedFee) : null,
		color: ticketForm.color,
	};

	return _ticketForm;
};

export const getBooleanString = (boolean: boolean): BooleanString => {
	const booleanString: BooleanString = boolean ? 'true' : 'false';
	return booleanString;
};

export const fillForm = (ticket: TicketBody | undefined, timezone: string) => {
	const ticketCamel = camelizeKeys(ticket, {
		// This function keeps ID as ID
		process: keepUpperCase,
	});
	if (ticketCamel) {
		const _form = {
			...ticketCamel,
			poolId: typeof ticketCamel.ticketsPoolId === 'number' ? ticketCamel.ticketsPoolId : undefined,
			buyerAbsorbPaymentFee: getBooleanString(ticketCamel.buyerAbsorbPaymentFee),
			buyerAbsorbTixologistFee: getBooleanString(ticketCamel.buyerAbsorbTixologistFee),
			sellingTime: convertUTCToDate(ticketCamel.sellingTime, timezone),
			offSellingTime: ticketCamel.offSellingTime ? convertUTCToDate(ticketCamel.offSellingTime, timezone) : null,
			template: ticketCamel.image,
			ticketImage: ticketCamel.image,
			displayType: ticketCamel.template.displayType,
			ticketVisibility: ticketCamel.ticketVisibility,
			privateLink: !!ticketCamel.hiddenCode,
			hiddenCode: ticketCamel.hiddenCode,
			showImage: ticketCamel.showImage ?? false,
			seatsCategoryKey: ticketCamel.seatsCategoryKey ? String(ticketCamel.seatsCategoryKey) : undefined,
			presaleCode: ticketCamel.presaleCode || '',
			alwaysShowTickets: ticketCamel.alwaysShowTickets ?? false,
			payWhatYouWant: ticketCamel.payWhatYouWant,
			minPrice: ticketCamel.minPrice,
			maxPrice: ticketCamel.maxPrice,
			maxQuantity: ticketCamel.maxQuantity,
			percentageFee: ticketCamel.percentageFee ?? null,
			fixedFee: ticketCamel.fixedFee ?? null,
			color: ticketCamel.color,
		};
		return _form;
	}
};

export const getTicketTypeCanGoLive = (eventCanGoLive: EventCanGoLive, ticketTypeId?: number): EventCanGoLive => {
	const ticketsDetails = ticketTypeId
		? { [ticketTypeId]: eventCanGoLive.ticketTypeDetails[ticketTypeId] }
		: { 0: true };

	return {
		hasTickets: true,
		eventDetails: true,
		allTicketDetails: true,
		ticketTypeDetails: ticketsDetails,
		partnerInfo: eventCanGoLive.partnerInfo,
	};
};

export const showReqFieldsModal = (event: EventData, eventCanGoLive: EventCanGoLive, ticketPrice: number): boolean => {
	const isFreeEvent = event.ticketTypes.every((ticket) => ticket.initialPrice === 0);
	const eventIsLive = event.liveOnWidget || event.liveOnWebpage;
	const missingInfo = !eventCanGoLive.partnerInfo.taxInfo || !eventCanGoLive.partnerInfo.payoutsInfo;
	if (eventIsLive && isFreeEvent && ticketPrice > 0 && missingInfo) {
		return true;
	}

	return false;
};
